import React, { useMemo } from "react";
import { Chart as ChartJS } from "chart.js";
import {
  ChoroplethController,
  ColorScale,
  geoConicConformal,
  GeoFeature,
  ProjectionScale,
} from "chartjs-chart-geo";
import { Chart } from "react-chartjs-2";
import { feature } from "topojson-client";
import world from "world-atlas/countries-50m.json";

ChartJS.register(ChoroplethController, GeoFeature, ProjectionScale, ColorScale);

const GuestsOriginsChart = ({ stats }) => {
  const EUROPE_BOUNDS = {
    minLon: -25,
    maxLon: 45,
    minLat: 30,
    maxLat: 72,
  };

  const isPolygonInEurope = (polygon) => {
    const coordinates = polygon.flat();
    const longitudes = coordinates.map((point) => point[0]);
    const latitudes = coordinates.map((point) => point[1]);

    const minLon = Math.min(...longitudes);
    const maxLon = Math.max(...longitudes);
    const minLat = Math.min(...latitudes);
    const maxLat = Math.max(...latitudes);

    return (
      maxLon >= EUROPE_BOUNDS.minLon &&
      minLon <= EUROPE_BOUNDS.maxLon &&
      maxLat >= EUROPE_BOUNDS.minLat &&
      minLat <= EUROPE_BOUNDS.maxLat
    );
  };

  const sanitizeEuropeanFeature = (country) => {
    const geometry = country.geometry;
    if (!geometry) return null;

    if (geometry.type === "MultiPolygon") {
      const europeanPolygons = geometry.coordinates.filter(isPolygonInEurope);
      if (!europeanPolygons.length) return null;

      return {
        ...country,
        geometry: {
          ...geometry,
          coordinates: europeanPolygons,
        },
      };
    }

    if (geometry.type === "Polygon") {
      return isPolygonInEurope(geometry.coordinates) ? country : null;
    }

    return country;
  };

  const countries = feature(world, world.objects.countries).features;

  const EUROPE_IDS = [
    40, 56, 100, 191, 203, 208, 233, 246, 250, 276, 300, 348, 352, 372, 380,
    428, 440, 442, 470, 498, 528, 578, 616, 620, 642, 688, 703, 705, 724, 752,
    756, 826,
  ];

  const europeCountries = countries
    .filter((c) => EUROPE_IDS.includes(+c.id))
    .map(sanitizeEuropeanFeature)
    .filter(Boolean);

  const europeOutline = {
    type: "FeatureCollection",
    features: europeCountries,
  };
  const europeProjection = geoConicConformal()
    .center([15, 54])
    .parallels([35, 65]);

  const geoMapData = useMemo(() => {
    if (!stats?.guests_geo_info) return null;

    const geoMapInfo = new Map(
      stats?.guests_geo_info.map((g) => [Number(g.numeric_code), g.value])
    );

    return {
      labels: europeCountries.map((d) => d.properties.name),
      datasets: [
        {
          outline: europeOutline,
          data: europeCountries.map((country) => ({
            feature: country,
            value: geoMapInfo.get(Number(country.id)) || 0,
          })),
        },
      ],
    };
  }, [stats, europeCountries]);

  return (
    <>
      {geoMapData && (
        <div className="w-100 mb-2 mb-md-4">
          <div className="card p-2 p-md-4">
            <h5 className="mb-3">Herkunft der Gäste</h5>
            <div style={{ height: "500px" }}>
              <Chart
                type="choropleth"
                data={geoMapData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  clipMap: "chartArea",
                  scales: {
                    projection: {
                      axis: "x",
                      projection: europeProjection,
                      projectionScale: 1.05,
                      padding: 20,
                    },
                    color: {
                      axis: "x",
                      legend: {
                        position: "bottom-right",
                      },
                    },
                  },
                  plugins: {
                    legend: { display: false },
                    tooltip: {
                      filter: (context) => {
                        return context.raw?.value > 0;
                      },
                      callbacks: {
                        label: (ctx) => {
                          const value = ctx.raw.value;
                          const name = stats.guests_geo_info.find(
                            (g) =>
                              Number(g.numeric_code) ===
                              Number(ctx.raw.feature.id)
                          )?.name;

                          return `${name}: ${value}`; // Gäste
                        },
                      },
                    },
                  },
                }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GuestsOriginsChart;
